// Compose a Speediance push payload for a COACH-prescribed strength session.
//
// A coach session carries its OWN exercise list with EXPLICIT per-set
// weights, so composition is a straight projection:
//
//   - Resolve each exercise's (groupId, variantId) from its `group_id`: the
//     operator's mapping row for the group picks the variant (Settings →
//     Speediance); an unmapped group falls back to the catalog's first
//     variant (the catalog lists English-named variants first). A group in
//     neither has no Speediance identity we can trust → skipped.
//   - Send the prescribed per-set weights VERBATIM (no ramp).
//   - Coach exercises with no `group_id` (free-form / bodyweight) have no
//     Speediance identity → skipped, surfaced to the operator rather than
//     silently dropped.
//
// Pushed BY NAME ('Protocol — Coach') with no templateId — the proxy's
// find-by-name idempotency updates the single shared coach template (or creates
// it the first time).
//
// Unilateral handling lives in the proxy (buildActionObj doubles L/R off the
// library's is_unilateral), so we send one logical set per prescribed set.

import { roundToWholeKg } from './protocol';
import type {
  SpeedianceExercisePayload,
  SpeedianceLibraryEntry,
  SpeedianceSessionPayload,
} from './speediance-client';
import type { CoachStrengthSession, SpeedianceMappingRow } from './types';

/** Stable Speediance template name for coach sessions (single reusable slot). */
export const COACH_TEMPLATE_NAME = 'Protocol — Coach';

/** Default rest (s) — the coach schema doesn't carry per-set rest. */
const DEFAULT_REST_S = 90;

/** A pushable exercise, with a compact set label for display. */
export type CoachPushExercise = {
  name: string;
  /** e.g. "3 × 8×40 …" — first set's reps×kg, with a trailing ellipsis when >1. */
  label: string;
};

export type CoachPushComposition = {
  payload: SpeedianceSessionPayload;
  /** The exercises that made it into `payload`, in order, with display labels.
   *  Render the UI list from THIS (not a re-derived filter) so the displayed
   *  exercises always match what actually gets pushed. */
  pushable: CoachPushExercise[];
  /** Exercises that couldn't be pushed (no group_id, unknown group, or no valid sets). */
  skipped: Array<{ name: string; reason: string }>;
};

/**
 * Compose the push payload for a coach session. `defaultMode` is the operator's
 * Speediance default (config.default_mode), used when an exercise doesn't carry
 * its own `speediance_mode`.
 */
export function composeCoachSessionPayload(
  session: CoachStrengthSession,
  mappingRows: SpeedianceMappingRow[],
  catalog: SpeedianceLibraryEntry[],
  defaultMode: number,
): CoachPushComposition {
  const variantByGroup = new Map<number, number>();
  for (const m of mappingRows) variantByGroup.set(m.group_id, m.variant_id);

  const exercises: SpeedianceExercisePayload[] = [];
  const pushable: CoachPushExercise[] = [];
  const skipped: Array<{ name: string; reason: string }> = [];

  for (const ex of session.exercises) {
    if (ex.group_id === undefined) {
      skipped.push({ name: ex.name, reason: 'no Speediance exercise (free-form / bodyweight)' });
      continue;
    }
    const groupId = ex.group_id;
    const variantId =
      variantByGroup.get(groupId) ?? catalog.find((e) => e.id === groupId)?.variants[0]?.id;
    if (variantId === undefined) {
      skipped.push({ name: ex.name, reason: 'group not in the Speediance catalog — re-sync the library' });
      continue;
    }
    const mode = ex.speediance_mode ?? defaultMode;
    // The proxy rejects reps <= 0 (a non-positive rep count). `reps` is optional
    // on the coach schema, so drop any set without a positive rep target rather
    // than emitting reps:0 (which would 400 the whole push). This also drops
    // TIMED sets (duration_s holds — planks etc.): the device has no timed-set
    // concept, and holds are bodyweight work the operator does off-device.
    const sets = ex.sets
      .filter((s) => Number.isFinite(s.reps) && (s.reps ?? 0) > 0)
      .map((s) => ({
        reps: s.reps as number,
        // Speediance combined load is whole-kg only — round so the pushed
        // target is actually loadable (a 12.5 would clamp to 12 on the device,
        // making the target-vs-actual drift). Round here defensively even
        // though the coach is now prompted to prescribe whole kg.
        weight: roundToWholeKg(s.weight_kg),
        rest: DEFAULT_REST_S,
        mode,
      }));
    if (sets.length === 0) {
      skipped.push({ name: ex.name, reason: 'no sets with a rep target' });
      continue;
    }
    exercises.push({ groupId, variantId, sets });
    const first = sets[0]!;
    const kg = Number.isInteger(first.weight) ? `${first.weight}` : first.weight.toFixed(1);
    pushable.push({
      name: ex.name,
      label: `${sets.length} × ${first.reps}×${kg}${sets.length > 1 ? ' …' : ''} kg`,
    });
  }

  return {
    payload: { name: COACH_TEMPLATE_NAME, exercises },
    pushable,
    skipped,
  };
}
